import { Link } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import type { PrepaidRunEntity } from '@dcc-erp/shared';
import { fmt, fmtDate } from '@dcc-erp/shared';

import { runRangeLabel } from './pp-helpers';
import { ReverseRunModal } from './ReverseRunModal';
import {
  useAmortizationRunEntities,
  useApproveAmortizationRun,
  useRejectAmortizationRun,
  useReverseAmortizationRun,
} from '../../lib/queries';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';

// ============================================================
// Run governance — first-class PrepaidAmortizationRun entities with their
// approval chain. Maker submits → reviewer/approver act here (Approve/Reject via
// the shared DecisionModal) → on final approval the batch JV posts; a POSTED run
// can be reversed (FB08 mirror JV) with a required reason. Reused on the Journal
// tab above the JV-posting history.
// ============================================================

function runStatusBadge(run: PrepaidRunEntity) {
  if (run.status === 'POSTED')
    return (
      <span className="badge positive">
        <span className="bdot" />
        ผ่านอนุมัติ
      </span>
    );
  if (run.status === 'REVERSED')
    return (
      <span className="badge neutral">
        <span className="bdot" />
        กลับรายการแล้ว
      </span>
    );
  // Design-Z: a rejected run keeps accounting status PENDING_APPROVAL; only the
  // engine approvalStatus flips to REJECTED. Surface it so a dead run doesn't
  // masquerade as still-pending.
  if (run.approvalStatus === 'REJECTED')
    return (
      <span className="badge negative">
        <span className="bdot" />
        ไม่อนุมัติ
      </span>
    );
  return (
    <span className="badge warning">
      <span className="bdot" />
      รออนุมัติ
    </span>
  );
}

export function PPRunGovernance({ companyId }: { companyId: string | undefined }) {
  const { data: runs = [] } = useAmortizationRunEntities(companyId);
  const approveMut = useApproveAmortizationRun(companyId);
  const rejectMut = useRejectAmortizationRun(companyId);
  const reverseMut = useReverseAmortizationRun(companyId);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [decideRun, setDecideRun] = useState<PrepaidRunEntity | null>(null);
  const [reverseTarget, setReverseTarget] = useState<PrepaidRunEntity | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleDecide(d: Decision) {
    if (!decideRun) return;
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: decideRun.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        // reject + return both end the run without posting — a prepaid run has no
        // resubmit lane (the maker simply submits a fresh run), so a returned
        // decision is recorded as a reject.
        await rejectMut.mutateAsync({
          id: decideRun.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setDecideRun(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'ดำเนินการไม่สำเร็จ');
    }
  }

  // Hidden until the first governed run exists (pre-entity history shows below).
  if (runs.length === 0) return null;

  return (
    <div className="tbl-shell" style={{ marginBottom: 16 }}>
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          รอบประมวลผลตัดจ่าย (อนุมัติ / กลับรายการ)
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>ช่วงงวด</th>
            <th className="num">จำนวนสัญญา</th>
            <th className="num">ยอดตัดจ่าย</th>
            <th>สถานะ</th>
            <th>ผู้ส่ง / ผลอนุมัติ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            // Actionable only while the ENGINE state is PENDING — a rejected run
            // keeps accounting status PENDING_APPROVAL (Design-Z) but must NOT
            // show the พิจารณา action or highlight the timeline.
            const isPending = run.approvalStatus === 'PENDING';
            const open = expandedId === run.id;
            return (
              <Fragment key={run.id}>
                <tr>
                  <td>
                    <b>{runRangeLabel(run)}</b>
                    {run.isAuto && (
                      <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                        (อัตโนมัติ)
                      </span>
                    )}
                  </td>
                  <td className="num">{run.contractsProcessed}</td>
                  <td className="num tnum">
                    <b>{fmt(Number(run.totalAmount))}</b>
                  </td>
                  <td>{runStatusBadge(run)}</td>
                  <td className="muted">
                    {run.status === 'REVERSED'
                      ? `กลับโดย ${run.reversedBy ?? '—'}`
                      : run.status === 'POSTED'
                        ? `อนุมัติ ${run.approvedBy ?? '—'}`
                        : `ส่งโดย ${run.submittedBy ?? '—'}`}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn sm ghost"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => setExpandedId(open ? null : run.id)}
                      >
                        {open ? 'ซ่อน' : 'สายอนุมัติ'}
                      </button>
                      {isPending && (
                        <button
                          className="btn sm"
                          style={{ height: 28, fontSize: 14 }}
                          onClick={() => {
                            setActionError(null);
                            setDecideRun(run);
                          }}
                        >
                          พิจารณา
                        </button>
                      )}
                      {run.status === 'POSTED' && (
                        <>
                          {run.batchJournalEntryId && (
                            <Link
                              to="/journal"
                              className="btn sm ghost"
                              style={{ height: 28, fontSize: 14 }}
                            >
                              ดู JV
                            </Link>
                          )}
                          <button
                            className="btn sm"
                            style={{ height: 28, fontSize: 14 }}
                            onClick={() => {
                              setActionError(null);
                              setReverseTarget(run);
                            }}
                          >
                            กลับรายการ
                          </button>
                        </>
                      )}
                      {run.status === 'REVERSED' && run.reversalJournalEntryId && (
                        <Link
                          to="/journal"
                          className="btn sm ghost"
                          style={{ height: 28, fontSize: 14 }}
                        >
                          ดู JV กลับรายการ
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={6} style={{ background: '#fafafa' }}>
                      <ApprovalTimeline
                        approvals={run.approvals}
                        isActive={isPending}
                        currentStep={run.currentStep}
                        leadLine={
                          run.submittedBy ? (
                            <div className="meta">
                              ส่งอนุมัติโดย {run.submittedBy}
                              {run.submittedAt
                                ? ` · ${fmtDate(String(run.submittedAt).slice(0, 10))}`
                                : ''}
                            </div>
                          ) : undefined
                        }
                        trailLine={
                          run.status === 'POSTED' && run.approvedBy ? (
                            <div className="meta">
                              อนุมัติโดย {run.approvedBy}
                              {run.approvedAt
                                ? ` · ${fmtDate(String(run.approvedAt).slice(0, 10))}`
                                : ''}
                            </div>
                          ) : run.status === 'REVERSED' && run.reversedBy ? (
                            <div className="meta">
                              กลับรายการโดย {run.reversedBy}
                              {run.reversedAt
                                ? ` · ${fmtDate(String(run.reversedAt).slice(0, 10))}`
                                : ''}
                              {run.reversalReason ? ` — ${run.reversalReason}` : ''}
                            </div>
                          ) : undefined
                        }
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {decideRun && (
        <DecisionModal
          voucherNo={`รอบ ${runRangeLabel(decideRun)}`}
          step={decideRun.currentStep || 1}
          busy={approveMut.isPending || rejectMut.isPending}
          error={actionError}
          titlePrefix="พิจารณารอบตัดจ่าย"
          hideReturn
          onConfirm={(d) => void handleDecide(d)}
          onClose={() => {
            setDecideRun(null);
            setActionError(null);
          }}
        />
      )}

      {reverseTarget && (
        <ReverseRunModal
          run={reverseTarget}
          busy={reverseMut.isPending}
          error={actionError}
          onConfirm={(reason) => {
            setActionError(null);
            reverseMut.mutate(
              { id: reverseTarget.id, reason },
              {
                onSuccess: () => setReverseTarget(null),
                onError: (e) =>
                  setActionError(e instanceof Error ? e.message : 'กลับรายการไม่สำเร็จ'),
              },
            );
          }}
          onClose={() => {
            setReverseTarget(null);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}
